import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UserService } from './user.service';
import { User } from '../_models/User';
import { BaseService } from '../_shared/base.service';

import { Observable, of } from 'rxjs';
import { map, mergeMap, catchError } from 'rxjs/operators';

import { first } from 'rxjs/operators';
import { getUrl } from '../_shared/utils';
import { JWTTokenService } from './jwtToken.service';
import { EngineService } from './engine.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthService extends BaseService{


  private currentUserCached: User;
  private apiUrl = getUrl('auth/');

  public getCurrentUser(): Observable<User> {
    const authInfo = this.jwtTokenService.decodeToken('portal');
    if (authInfo) {
      if (this.currentUserCached) {
        return of(this.currentUserCached);
      } else {
        localStorage.setItem('vendorName', authInfo.vendorName);
        return this.userService.getUser(authInfo.nameid)
          .pipe(map(user => {
            this.currentUserCached = user;
            return user;
          }));
      }
    } else {
      return this.logout().pipe(map(() => { return null }));
    }
  }


  constructor(
    private http: HttpClient,
    private userService: UserService,
    private engineService: EngineService,
    jwtTokenService: JWTTokenService,
    private router: Router) {
      super(http, jwtTokenService);
  }

  public logout() {

    const clearLocalSessionData = () => {

      this.currentUserCached = null;
      this.userService.userPermissions = {};
      this.engineService.clearServiceTypeCache();
      this.jwtTokenService.clearToken('portal');
      this.clearHomeRoute();
      BaseService.GlobalClearCache();
    }

    return this.http.get<any>(this.apiUrl + `logout`, this.requestOptionsAuthenticated('portal'))
      .pipe(map(() => clearLocalSessionData()),
      catchError((error, caught) => of(clearLocalSessionData())));
  }

  login(email: string, password: string): Observable<any> {
    const loginData = { email, password };
    return this.http.post<any>(this.apiUrl + 'login', loginData, this.requestOptions()).pipe(mergeMap(response => {
      this.jwtTokenService.setToken('portal', response.loginTokenString);
      localStorage.setItem('HomeRoute', response.homeRoute);

      return of(response);
    }));
  }

  public register(email: string, password: string) {
    throw new Error("authervice: Auth.Register has not been implemented yet");
    const loginData = { email, password };
    return this.http.post<any>(this.apiUrl + 'register', loginData, this.requestOptionsAuthenticated('onboarding'));
  }

  public activateVendorAccount(): Observable<any> {
    return this.http.get<any>(getUrl('vendor/activate'), this.requestOptionsAuthenticated('stripe-onboarding'));
  }

  async getUserInfo(): Promise<{ enableSubscription: boolean }> {
    const userInfo = {
      enableSubscription: false
    };

    const user = await this.getCurrentUser().pipe(first()).toPromise();

    if (user && user.subscriptionEndDate && user.subscriptionStartDate) {
      userInfo.enableSubscription = true;
    }

    return userInfo;
  }

  forgotPassword(email: string) {
    return this.http.get(this.apiUrl + 'forgot-password/' + email, this.requestOptions()).pipe(map((response: Response) => {
      return response;
    }));
  }

  /* forgotPasswordAdmin(model: any) {
    model['HostUrl'] = window.location.hostname;
    return this.http.post(this.apiUrl + 'forgot-password-admin', model, this.requestOptionsAuthenticated('portal'))
      .map((response: Response) => {
      return response;
    });
  } */

  getUserEmail(): string {
    const decode = this.jwtTokenService.decodeToken('portal');

    return decode ? decode.unique_name : '';
  }

  resetPassword(email: string, password: string) {
    const model = { email, password };
    return this.http.post(this.apiUrl + 'reset-password', model, this.requestOptionsAuthenticated('forgotPassword'));
  }

  loggedIn(): Observable<boolean> {
    if (this.jwtTokenService.isTokenExpired('portal')) {
      return this.http.get<any>(this.apiUrl + `check-refresh-token`, this.requestOptions())
        .pipe(map(
          response => {
            const clientAppVersion = localStorage.getItem('Application-Version');
            const serverAppVersion = response.applicationVersion;
            if (serverAppVersion && clientAppVersion !== serverAppVersion) {
              localStorage.setItem('Application-Version', serverAppVersion);
              location.reload();
            }

            if (response.loginTokenString) {
              this.jwtTokenService.setToken('portal', response.loginTokenString);
              return true;
            }

            return false;
          }),
          catchError((err, caught) => {

            return of(false);
          }));
    } else {
      return of(true);
    }
  }

  public checkFeatureFlag(featureName: string): Observable<boolean>  {
    return this.CachedHttpGet<boolean>(`auth/check-flag/${featureName}`);
  }

  saveAssets(model: any) {
    return this.http.post(getUrl('assets/save-assets'), model, this.requestOptionsAuthenticated('portal'));
  }

  getAvailableTimeZones() {
    return this.http.get<any[]>(this.apiUrl + 'time-zones', this.requestOptionsAuthenticated('portal'));
  }

  public getS3PresignedUrl(objectKey: string, mimeType: string, endpoint: string): Observable<string> {
    const model = { fileObjectKey: objectKey, mimeType };
    const token = 'Bearer ' + this.jwtTokenService.getToken('portal');
    const headers = new HttpHeaders({ 'Content-type': 'application/json', Authorization: token });
    const options = { headers: headers, responseType: 'text' as 'text' };
    return this.http.post(this.apiUrl + endpoint, model, options);
  }

  private requestOptions() {
    const headers = new HttpHeaders({ 'Content-type': 'application/json' });
    return { headers: headers, responseType: 'json' as 'json', withCredentials: true };
  }

  postFile(fileToUpload: File) {
    const ro = this.requestOptionsAuthenticated('portal');
    const endpoint = this.apiUrl + 'trackFile';
    const formData: FormData = new FormData();
    formData.append('trackFile', fileToUpload, fileToUpload.name);
    return this.http.post(endpoint, formData, ro);
  }

  public getHomeRoute(): string {
    const homeRoute = localStorage.getItem('HomeRoute');
    return homeRoute ?? '';
  }

  public clearHomeRoute() {
    localStorage.setItem('HomeRoute', '');
  }

  private requestOptionsAuthenticated(tokenKey: string) {
    const requestOptions = {
        headers: new HttpHeaders()
            .set('Content-type', 'application/json')
            .append('Authorization', 'Bearer ' + this.jwtTokenService.getToken(tokenKey)),
    };
    return requestOptions;
}
}

export interface LoginInfo {
  email: string;
  password: string;
}
